import { Flex } from '@repo/ui/Flex';
import type { SubmitStatus } from '../../graphql/mutation/submit';
import { Text } from '../Language/Language';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';

import s from './ReturnArtifacts.module.css';
import type { PollingStatus } from '../Complete/useLabelPolling';
import type { DeliveryMethod, ReturnArtifact } from './types';
import { ReturnLabelButton } from './ReturnLabelButton';
import { ReturnQRCode } from './ReturnQRCode';
import { Button } from '@repo/ui/Button';
import { usePackagingOption } from './PackagingOptionContext';

export const ReturnArtifacts = ({
  label,
  qrCode,
  deliveryMethod,
  status,
  pollingStatus,
}: {
  label?: ReturnArtifact;
  qrCode?: ReturnArtifact;
  deliveryMethod?: DeliveryMethod;
  status: SubmitStatus;
  pollingStatus?: PollingStatus;
}) => {
  const isMobile = useIsMobile();
  const { packagingOption, setPackagingOption } = usePackagingOption();
  const labelUrl = label?.url;
  const qrCodeUrl = qrCode?.url;
  const packagingOptionFromDelivery = deliveryMethod?.packagingOption;

  const isPolling =
    pollingStatus === 'polling' &&
    ['LABEL', 'PRINTERLESS', 'BOXLESS'].includes(
      packagingOptionFromDelivery ?? 'NONE',
    );

  const isError =
    (pollingStatus === 'error' || pollingStatus === 'disabled') &&
    !labelUrl &&
    !qrCodeUrl &&
    ['LABEL', 'PRINTERLESS', 'BOXLESS'].includes(
      packagingOptionFromDelivery ?? 'NONE',
    );

  const canSwitch = !!labelUrl && !!qrCodeUrl;

  return (
    <Flex className={s.psslWrapper} direction="column" gap="g-16">
      {packagingOption === 'PRINTERLESS' ? (
        <ReturnQRCode qrCode={qrCode} isPolling={isPolling} />
      ) : (
        <ReturnLabelButton
          label={label}
          deliveryMethod={deliveryMethod}
          status={status}
          pollingStatus={pollingStatus}
          isMobile={isMobile}
          isPolling={isPolling}
          isError={isError}
        />
      )}
      {!isPolling && !isError && (
        <Text
          langKey={[
            `pdx.confirmation.artifacts.${packagingOption?.toLowerCase()}.description`,
            `pdx.confirmation.artifacts.${packagingOptionFromDelivery?.toLowerCase()}.description`,
            `pdx.confirmation.artifacts.description`,
          ]}
        />
      )}

      {canSwitch && (
        <Button
          className={s.switchLink}
          onClick={() =>
            setPackagingOption(
              packagingOption === 'PRINTERLESS' ? 'LABEL' : 'PRINTERLESS',
            )
          }
          variant="plain"
        >
          <Text
            langKey={
              packagingOption === 'PRINTERLESS'
                ? 'pdx.confirmation.switch_to_label'
                : 'pdx.confirmation.switch_to_printerless'
            }
          />
        </Button>
      )}
      {isError && (
        <>
          <h2 className={s.errorTitle}>
            <Text langKey="pdx.confirmation.artifacts.no_artifacts.title" />
          </h2>
          <div className={s.errorDescription}>
            <Text langKey="pdx.confirmation.artifacts.no_artifacts.description" />
          </div>
        </>
      )}
    </Flex>
  );
};
